import { Link } from "react-router-dom";
import { useLocalization } from "../../hooks/useLocalization";
import { useCookieConsent } from "../../context/CookieConsentProvider";
import { useLocalePath } from "../../hooks/useLocalePath";
import { useState } from "react";

const SOCIAL_LINKS = [
  {
    href: "https://x.com/FinnHouseLoL",
    icon: "twitter-logo.png",
    label: "X (Twitter)",
  },
  {
    href: "https://www.instagram.com/finnhouselol/",
    icon: "ig-logo.png",
    label: "Instagram",
  },
  {
    href: "https://www.twitch.tv/finnhouse",
    icon: "twitch-logo.png",
    label: "Twitch",
  },
  {
    href: "https://www.youtube.com/channel/UCLRtNqAkGhLO0--pLhDuuVA",
    icon: "youtube-logo.png",
    label: "YouTube",
  },
  {
    href: "https://discord.com/invite/Cup7umkhTX",
    icon: "dc-logo.png",
    label: "Discord",
  },
] as const;

const SPONSORS = [
  {
    href: "https://www.lastonline.fi",
    src: "partners/last-online-logo-black.svg",
    alt: "Last Online Logo",
  },
  {
    href: "https://www.tampere.fi",
    src: "partners/tampere-logo.png",
    alt: "Tampereen kaupunki logo",
  },
  {
    href: "https://tr3gamers.fi",
    src: "partners/TR3_gamers-logo.png",
    alt: "TR3 Gamers logo",
  },
  {
    href: "https://drafter.lol",
    src: "partners/drafterlol.png",
    alt: "Drafterlol",
  },
] as const;

export const Footer = () => {
  const { strings } = useLocalization();
  const { localized } = useLocalePath();
  const { openSettings } = useCookieConsent();
  const [discordCopied, setDiscordCopied] = useState(false);
  const [mailCopied, setMailCopied] = useState(false);
  const imgs = import.meta.env.VITE_S3_ASSET_URL;
  const year = new Date().getFullYear();

  const copy = (value: string, setCopied: (b: boolean) => void) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch((err: unknown) => {
        console.error("Copy failed:", err);
      });
  };

  const legalLinks = [
    { label: strings.FOOTER.TERMS, to: localized("/terms") },
    { label: strings.FOOTER.PRIVACY, to: localized("/privacy") },
    { label: strings.FOOTER.COOKIES, action: "cookies" as const },
  ];

  const copyBtnBase =
    "inline-flex w-fit max-w-full items-center justify-center rounded-round border border-white/[0.14] bg-white/[0.06] px-4 py-[0.55rem] font-jetbrains text-[clamp(0.75rem,1vw,0.85rem)] leading-[1.2] text-white/[0.92] cursor-pointer transition-[border-color,background-color,color,transform] duration-200 ease-in-out hover:-translate-y-px hover:border-[rgba(28,152,217,0.5)] hover:bg-[rgba(28,152,217,0.12)] hover:text-light-blue max-[600px]:[overflow-wrap:anywhere] max-[600px]:text-left";
  const copyBtnCopied = "border-[rgba(28,152,217,0.55)] text-light-blue bg-[rgba(28,152,217,0.1)]";

  return (
    <footer className="relative isolate w-full shrink-0 overflow-hidden border-t border-white/[0.08] bg-transparent px-[var(--padding-view)] py-[clamp(3rem,6vw,5rem)] box-border md:min-h-[36rem]">
      <img
        src={`${imgs}/icons/finnhouse.svg`}
        alt=""
        width={608}
        height={608}
        className="pointer-events-none absolute right-[-4%] bottom-[-8%] z-0 h-auto w-[clamp(16rem,32vw,38rem)] select-none opacity-[0.07] max-[600px]:right-[-15%] max-[600px]:bottom-[-5%] max-[600px]:w-[clamp(12rem,55vw,18rem)]"
        loading="lazy"
        decoding="async"
        aria-hidden="true"
      />

      <div className="relative z-[1] mx-auto flex w-full max-w-[75rem] flex-col gap-[clamp(2.5rem,5vw,3.5rem)]">
        <section className="text-center" aria-labelledby="footer-sponsors-title">
          <h2
            id="footer-sponsors-title"
            className="mb-[clamp(2.25rem,4vw,3rem)] font-shentox text-[clamp(0.95rem,1.4vw,1.1rem)] font-bold tracking-[0.08em] uppercase text-white/95"
          >
            {strings.FOOTER.SPONSORS}
          </h2>
          {/* Fixed card height + reserved grid min-height so partner logos cannot inflate the footer */}
          <div className="flex min-h-28 flex-wrap items-stretch justify-center gap-[clamp(1rem,2vw,1.5rem)] max-[600px]:min-h-[calc(7rem*4+1rem*3)] max-[600px]:flex-col max-[600px]:items-center">
            {SPONSORS.map((sponsor) => (
              <div
                key={sponsor.href}
                className="flex h-28 w-[min(100%,14rem)] shrink-0 items-center justify-center rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] p-[1rem_1.25rem] transition-[border-color,transform,background-color] duration-200 ease-in-out hover:-translate-y-0.5 hover:border-[var(--surface-card-hover-border)] max-[600px]:w-[min(100%,18rem)]"
              >
                <Link
                  className="flex h-full w-full items-center justify-center"
                  to={sponsor.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={`${imgs}/${sponsor.src}`}
                    alt={sponsor.alt}
                    width={160}
                    height={80}
                    loading="eager"
                    decoding="async"
                    className="aspect-[2/1] h-auto max-h-20 w-full object-contain"
                  />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-3 gap-[clamp(1.5rem,3vw,2.5rem)] max-[900px]:grid-cols-1 max-[900px]:gap-8">
          <section className="flex min-w-0 flex-col" aria-labelledby="footer-contact-title">
            <h2
              id="footer-contact-title"
              className="mb-4 font-shentox text-[clamp(0.95rem,1.4vw,1.1rem)] font-bold tracking-[0.08em] uppercase text-white/95"
            >
              {strings.FOOTER.CONTACT}
            </h2>
            <p className="mb-5 max-w-[22rem] font-jetbrains text-[clamp(0.85rem,1.1vw,0.95rem)] leading-[1.5] text-white/70 max-[900px]:max-w-none">
              {strings.FOOTER.CONTACT_DESCRIPTION}
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-start gap-[0.45rem]">
                <span className="font-jetbrains text-[0.75rem] tracking-[0.04em] uppercase text-white/55">
                  {strings.FOOTER.DISCORD}
                </span>
                <button
                  type="button"
                  className={`${copyBtnBase}${discordCopied ? ` ${copyBtnCopied}` : ""}`}
                  title={strings.FOOTER.COPY}
                  onClick={() => {
                    copy("floora", setDiscordCopied);
                  }}
                >
                  {discordCopied ? `${strings.FOOTER.COPIED} ✔` : "FLOORA"}
                </button>
              </div>

              <div className="flex flex-col items-start gap-[0.45rem]">
                <span className="font-jetbrains text-[0.75rem] tracking-[0.04em] uppercase text-white/55">
                  {strings.FOOTER.EMAIL}
                </span>
                <button
                  type="button"
                  className={`${copyBtnBase}${mailCopied ? ` ${copyBtnCopied}` : ""}`}
                  title={strings.FOOTER.COPY}
                  onClick={() => {
                    copy("finnhouse@gmail.com", setMailCopied);
                  }}
                >
                  {mailCopied ? `${strings.FOOTER.COPIED} ✔` : "FINNHOUSE@GMAIL.COM"}
                </button>
              </div>
            </div>
          </section>

          <section className="flex min-w-0 flex-col" aria-labelledby="footer-links-title">
            <h2
              id="footer-links-title"
              className="mb-4 font-shentox text-[clamp(0.95rem,1.4vw,1.1rem)] font-bold tracking-[0.08em] uppercase text-white/95"
            >
              {strings.FOOTER.LINKS}
            </h2>
            <div className="flex flex-wrap gap-3">
              {SOCIAL_LINKS.map((social) => (
                <Link
                  key={social.href}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] transition-[border-color,background-color,transform] duration-200 ease-in-out hover:-translate-y-0.5 hover:border-[var(--surface-card-hover-border)] hover:bg-[rgba(28,152,217,0.12)]"
                  to={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                >
                  <img
                    src={`${imgs}/icons/${social.icon}`}
                    alt=""
                    width={20}
                    height={20}
                    loading="lazy"
                    decoding="async"
                    className="h-5 w-5 invert opacity-90"
                  />
                </Link>
              ))}
            </div>
          </section>

          <section className="flex min-w-0 flex-col" aria-labelledby="footer-info-title">
            <h2
              id="footer-info-title"
              className="mb-4 font-shentox text-[clamp(0.95rem,1.4vw,1.1rem)] font-bold tracking-[0.08em] uppercase text-white/95"
            >
              {strings.FOOTER.INFO}
            </h2>
            <nav
              className="flex flex-col items-start gap-[0.65rem]"
              aria-label={strings.FOOTER.INFO}
            >
              {legalLinks.map((link) =>
                link.action === "cookies" ? (
                  <button
                    key={link.label}
                    type="button"
                    className="inline-block cursor-pointer border-0 border-b border-b-transparent bg-transparent p-0 pb-[0.15rem] text-left font-jetbrains text-[clamp(0.85rem,1.1vw,0.95rem)] text-white/[0.72] no-underline transition-[color,border-color] duration-200 ease-in-out hover:border-b-[rgba(28,152,217,0.6)] hover:text-light-blue"
                    onClick={openSettings}
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.label}
                    to={link.to}
                    className="inline-block pb-[0.15rem] border-b border-b-transparent font-jetbrains text-[clamp(0.85rem,1.1vw,0.95rem)] text-white/[0.72] no-underline transition-[color,border-color] duration-200 ease-in-out hover:border-b-[rgba(28,152,217,0.6)] hover:text-light-blue"
                  >
                    {link.label}
                  </Link>
                ),
              )}
            </nav>
          </section>
        </div>

        <p className="m-0 border-t border-white/[0.06] pt-[clamp(1rem,2vw,1.5rem)] text-center font-jetbrains text-[0.8rem] tracking-[0.03em] text-white/45">
          © {year} Finnhouse. {strings.FOOTER.COPYRIGHT}
        </p>
      </div>
    </footer>
  );
};
